import { Link } from "@ui/Atoms/Link/Link";
import { IconHeart } from "@ui/Icons/IconHeart";
import { EmbedCard } from "@ui/Molecules/Embed/Card/EmbedCard";
import { PlaceListRow } from "@ui/Molecules/Place/ListRow/PlaceListRow";
import { AppShell } from "@ui/Organisms/Layout/AppShell";
import type * as React from "react";

export type SavedPlace = {
	id: string;
	name: string;
	href: string;
	area?: string;
	walk?: string;
	cover?: string | null;
	views?: string;
	ago?: string;
};

export interface SavedTemplateTranslations {
	title: string;
	/** e.g. "3 plekken". */
	countLabel: (count: number) => string;
	emptyTitle: string;
	emptyBody: string;
	emptyCtaLabel: string;
	emptyEmbedLabel: string;
}

type Props = {
	topBar: React.ReactNode;
	tabBar: React.ReactNode;
	translations: SavedTemplateTranslations;
	places: SavedPlace[];
	emptyCtaHref: string;
};

export function SavedTemplate({
	topBar,
	tabBar,
	translations: t,
	places,
	emptyCtaHref,
}: Props) {
	return (
		<AppShell topBar={topBar} tabBar={tabBar}>
			<div className="mx-auto max-w-[1360px] px-5 pb-24 pt-6 md:px-10 md:pt-11">
				<div className="mb-6 flex items-baseline gap-3.5 border-b border-[var(--border-strong)] pb-4">
					<h1
						className="m-0 text-[20px] font-extrabold tracking-[-0.03em] md:text-[34px]"
						style={{ fontFamily: "var(--font-display)" }}
					>
						{t.title}
					</h1>
					<span className="text-[13px] font-semibold text-[var(--content-faint)] md:text-[15px]">
						{t.countLabel(places.length)}
					</span>
				</div>

				{places.length === 0 ? (
					<div className="flex flex-col items-center gap-3.5 px-5 py-16 text-center md:py-20">
						<span className="grid h-16 w-16 place-items-center rounded-[var(--radius-pill)] bg-[var(--surface-elevated)]">
							<IconHeart size={27} color="var(--content-faint)" />
						</span>
						<h2
							className="m-0 text-[18px] font-extrabold tracking-[-0.025em] md:text-[22px]"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{t.emptyTitle}
						</h2>
						<p className="m-0 max-w-[38ch] text-[14px] leading-[1.55] text-[var(--content-muted)] md:text-[15px]">
							{t.emptyBody}
						</p>
						<Link
							href={emptyCtaHref}
							className="mt-1.5 flex h-11 items-center rounded-[var(--radius-sm)] bg-[var(--surface-inverted)] px-5 text-[14px] font-bold text-[var(--content-inverted)]"
						>
							{t.emptyCtaLabel}
						</Link>
					</div>
				) : (
					<>
						<div className="hidden grid-cols-2 gap-6 md:grid md:grid-cols-4">
							{places.map((place) => (
								<EmbedCard
									key={place.id}
									id={place.id}
									name={place.name}
									href={place.href}
									area={place.walk ?? place.area}
									cover={place.cover}
									views={place.views}
									ago={place.ago}
									variant="grid"
									emptyLabel={t.emptyEmbedLabel}
								/>
							))}
						</div>
						<div className="flex flex-col md:hidden">
							{places.map((place) => (
								<PlaceListRow
									key={place.id}
									size="sm"
									id={place.id}
									name={place.name}
									href={place.href}
									area={place.area?.split(",")[0]}
									distance={place.walk}
									cover={place.cover}
									emptyLabel={t.emptyEmbedLabel}
								/>
							))}
						</div>
					</>
				)}
			</div>
		</AppShell>
	);
}
